import { useState } from "react";
import { CalendarClock, CheckCircle2, Loader2, XCircle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** A passenger asked the assistant to move their pickup. Nothing moves until the office says so. */
export const ChangeRequestCard = ({ manifestId, trip, onDone }) => {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const ask = trip.change_request;
  if (!ask) return null;

  const decide = async (decision) => {
    setBusy(decision);
    setError("");
    try {
      await api.post(`/manifests/${manifestId}/change-request/${decision}`);
      onDone?.(decision);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="change-request-card"
      className="no-print rounded-xl border border-amber-400/50 bg-amber-400/5 p-5">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-amber-300">
        <CalendarClock className="h-3.5 w-3.5" /> Passenger change request
      </p>
      <p data-testid="change-request-ask" className="mt-3 text-sm text-slate-100">
        {ask.asked_by || "The passenger"} asked for{" "}
        <span className="font-semibold text-amber-200">{ask.trip_date} at {ask.pickup_time}</span>.
        Now booked for {trip.trip_date} at {trip.pickup_time}.
      </p>
      {ask.at && (
        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">
          Asked {new Date(ask.at).toLocaleString()}
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button data-testid="change-request-approve" size="sm" disabled={!!busy}
          onClick={() => decide("approve")}
          className="bg-emerald-500 text-ink-900 hover:bg-emerald-400">
          {busy === "approve" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />}
          Approve the new time
        </Button>
        <Button data-testid="change-request-decline" size="sm" variant="outline" disabled={!!busy}
          onClick={() => decide("decline")}
          className="border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10">
          {busy === "decline" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            : <XCircle className="mr-1.5 h-3.5 w-3.5" />}
          Keep it as it is
        </Button>
      </div>
      <p className="mt-3 text-[11px] text-slate-500">
        Either way the passenger is told by email and in their app.
      </p>
      {error && <p data-testid="change-request-error" className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  );
};
